import React, { useEffect, useRef, useState } from 'react';

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

/**
 * SearchInput — debounced text input (300ms) with clearable button.
 * Supports "/" global keyboard shortcut to focus.
 * Esc clears the input.
 */
export default function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
}: SearchInputProps) {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  // Hold latest onChange in a ref — parents typically pass an inline arrow
  // function, so including it in the debounce effect's deps would reset the
  // timer on every parent render and the debounce would never fire.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Update local state when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onChangeRef.current(localValue);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [localValue]);

  // Global "/" keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === '/' && e.target !== inputRef.current) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle Esc to clear
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setLocalValue('');
      onChange('');
    }
  }

  return (
    <div className="relative min-h-[44px] flex items-center">
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Search"
          className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {localValue && (
          <button
            onClick={() => {
              setLocalValue('');
              onChange('');
            }}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg-primary text-lg font-bold leading-none py-1 px-1"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}
